import fp from 'lodash/fp';
import React from 'react';
import { Button, Col, Collapse, Grid, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { LinkContainer } from 'react-router-bootstrap';
import { Link, Redirect } from 'react-router-dom';
import { push } from 'react-router-redux';
import { branch, compose, lifecycle, mapProps, renderComponent, renderNothing, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators } from 'redux';
import { routes, searchTileType, tabKeys, TaskStatus } from '../../../../constants';
import { findSubCpdCategorys, firstName, isManager, isMinimumBased, mapRecommendedContent, mapRecommendedResult, mapUserCpdCategoryProgress } from '../../../../services';
import { businessActions, businessSelector, publicNonCpdContentSelector } from '../../../../state/modules/business';
import { contentSelector } from '../../../../state/modules/content';
import { publisherActions } from '../../../../state/modules/publisher';
import { dashboardContentSelector, dashboardEventSelector, searchTileActions } from '../../../../state/modules/search-tile';
import { taskActions } from '../../../../state/modules/task';
import { tasksSelector } from '../../../../state/modules/task/selectors';
import { currentUserCpdWeeksRemaining, currentUserIndustry, currentUserSelector, userActions } from '../../../../state/modules/user';
import { noCapMap, pluralise } from '../../../../utils';
import { Icon } from '../../../components';
import Loading from '../../loading';
import SliderSection from '../partials/slider-section';
import { searchTileFilter } from '../training/partials/content-list';
import CpdCategory from './area-of-advice';
import OverallProgress from './overall-progress';
import PendingTasksIcon from './partials/pending-tasks-icon';
import PendingTasksTable from './pending-tasks-table';

const CPD_CATEGORY_CHUNK_SIZE = 6;

const getParentCpdCategoryLink = ({ name }) => {
  const categoryNameInitial = fp.first(name.split(' '))
  const link = routes.app.training[`${fp.lowerCase(categoryNameInitial)}Home`]
  if (link) {
    return link
  }
  return routes.app.training.home
}

const CpdCategoryChunk = ({ cpdCategory, isParent, aoaCount }) =>
  aoaCount === 5 ?
    (
      <Col xs={12} sm={4} className='col-ms-6 col-md-2-4'>
        <CpdCategory
          {...cpdCategory}
          redirectLink={isParent ? getParentCpdCategoryLink(cpdCategory) : routes.app.training.cpdCategory(cpdCategory.cpdCategoryId)} />
      </Col>
    ) :
    (
      <Col xs={12} sm={4} md={2} lg={2} className='col-ms-6'>
        <CpdCategory
          {...cpdCategory}
          redirectLink={isParent ? getParentCpdCategoryLink(cpdCategory) : routes.app.training.cpdCategory(cpdCategory.cpdCategoryId)} />
      </Col>
    )
  ;

const CollapsableCpdCategoryChunk = branch(
  ({ open }) => !open,
  renderNothing
)(({ open, ...props }) => (
  <Collapse in={open}>
    <CpdCategoryChunk {...props} />
  </Collapse>
));

const CpdCategorysRow = withProps(({ chunkNumber }) => ({
  CpdCategoryChunkType: chunkNumber === 0 ?
    CpdCategoryChunk :
    CollapsableCpdCategoryChunk
}))(({
  cpdCategorys,
  CpdCategoryChunkType,
  industryCpdCategorys,
  open
}) => (
    fp.map((cpdCategory) => (
      <CpdCategoryChunkType
        {...{
          key: cpdCategory.cpdCategoryId,
          isParent: findSubCpdCategorys(cpdCategory.cpdCategoryId, industryCpdCategorys).length > 0,
          aoaCount: cpdCategorys.length,
          cpdCategory,
          open
        }} />
    ))(cpdCategorys)
  ));

const CpdCategoryChunks = ({ cpdCategorysChunks, open, industryCpdCategorys }) => (
  <Row bsClass='flex-row' className='gutter-16 no-gutter'>
    {
      noCapMap((cpdCategorys, chunkNumber) => (
        <CpdCategorysRow
          {...{
            key: chunkNumber,
            open,
            cpdCategorys,
            chunkNumber,
            industryCpdCategorys: industryCpdCategorys
          }} />
      ))(cpdCategorysChunks)
    }
  </Row>
);

const WeeksRemaining = branch(
  ({ cpdWeeksRemaining }) => cpdWeeksRemaining === undefined,
  renderNothing
)(({ cpdWeeksRemaining }) => (
  <span>
    {cpdWeeksRemaining} {pluralise('week', cpdWeeksRemaining)} remaining
  </span>
));

const SetupPreferencesButton = branch(
  ({ userCpdCategorys }) => !fp.isEmpty(userCpdCategorys),
  renderNothing
)(() => (
  <div className='text-center'>
    <span className='instruction-text'>Start recording your progress</span>
    <LinkContainer to={routes.app.trainingPlansSettings(tabKeys.trainingPlansSettings.userTrainingPlan)}>
      <Button>Set up your CPD preferences</Button>
    </LinkContainer>
  </div>
));

const CpdCategorysAccordionButton = branch(
  ({ additionalCpdCategorys }) => fp.isEmpty(additionalCpdCategorys),
  renderNothing
)(({ toggleOpen }) => (
  <Button id='accordion--show-hide-areas' className='keyline centerBottom' onClick={toggleOpen}>
    <span className='btnShow'>
      <Icon icon='add-large' /> See all your CPD categories
    </span>
    <span className='btnHide'>
      <Icon icon='remove-large' /> See all your CPD categories
    </span>
  </Button>
));

const mapGoalProgress = ({ goal, total, completed, shortFall }, weeksRemaining) => {
  const progress = Math.min((100 * completed / total).toFixed(2), 100)
  return <Col key={goal.id} sm={4} md={2} className='flexbox vertical align-center'>
    <div id='cpd-progress-title'>{goal.name}</div>
    <LinkContainer className='clickable' to={routes.app.professionalProfile.progressReport}>
      <div>
        <OverallProgress {...{
          completed,
          totalProgressPercentage: progress,
          totalCpdPoints: total,
          shortFall
        }} />
      </div>
    </LinkContainer>
    <Link id='view-progress-link' to={routes.app.training.goal(goal.id)}>
      Earn CPD
    </Link>
    <Link id='view-progress-link' to={routes.app.professionalProfile.progressReport}>
      View Progress
    </Link>
    <div id='progress-weeks-remaining'>
      {weeksRemaining}
    </div>
  </Col>
}

/**
 * Application dashboard view for authenticated User.
 */
const Dashboard = ({
  userCpdCategorys,
  additionalCpdCategorys,
  userFirstName,
  open,
  toggleOpen,
  cpdCategorysChunks,
  industryCpdCategorys,
  isFetchingCpdCategorys,
  isFetchingPublishers,
  isFetchingContent,
  weeksRemaining,
  recommendedResult,
  mapContent,
  dashboardContent,
  dashboardEvent,
  nonCpdContent,
  userCpdCategoryProgress: {
    goals,
    primaryStat: {
      goal,
      validCpd
    },
    derivedStat: {
      progress,
      total,
      shortFall
    }
  },
  pendingUserTasks,
  goTo,
  industry
}) => (
    <div className='dashboard'>
      <section id='header--main'>
        <Grid>
          <Row className='sm-gutter'>
            {
              userCpdCategorys.length > 0 ?
                [
                  goal > 0 ? <Col key='cpd-progress' sm={4} md={2} className='flexbox vertical align-center'>
                    <div id='cpd-progress-title'>{fp.getOr('CPD Progress')('defaultProgressLabel')(industry)}</div>
                    <LinkContainer className='clickable' to={routes.app.professionalProfile.progressReport}>
                      <div>
                        <OverallProgress {...{
                          completed: goal - shortFall,
                          totalProgressPercentage: progress,
                          totalCpdPoints: goal,
                          shortFall
                        }} />
                      </div>
                    </LinkContainer>
                    <Link id='view-progress-link' to={routes.app.training.home}>
                      Earn CPD
                    </Link>
                    <Link id='view-progress-link' to={routes.app.professionalProfile.progressReport}>
                      View Progress
                    </Link>
                    <div id='progress-weeks-remaining'>
                      {weeksRemaining}
                    </div>
                  </Col> : null,
                  ...fp.map(
                    goal => mapGoalProgress(goal, weeksRemaining)
                  )(goals),
                  pendingUserTasks.length > 0 ?
                    <Col key='task-progress' sm={4} md={2}>
                      <div id='pending-tasks-title'>Pending Tasks</div>
                      <PendingTasksIcon tasks={pendingUserTasks} />
                      <div id='view-pending-tasks'>
                        <span onClick={() => goTo(routes.app.tasks.home)}>View tasks</span>
                      </div>
                    </Col> : null
                ] :
                <Col sm={8} md={6} smOffset={4} mdOffset={2}>
                  <h1>Hey {userFirstName}!</h1>
                  <p>Get started with Caddie by setting up your CPD preferences below</p>
                </Col>
            }
          </Row>
        </Grid>
      </section>
      {industryCpdCategorys.length > 1 || fp.isEmpty(userCpdCategorys) ?
        <section id={'accordion--areas-of-advice'} className={open ? 'opened' : ''}>
          <Grid>
            <h2>CPD to complete</h2>
            <CpdCategoryChunks cpdCategorysChunks={cpdCategorysChunks} open={open} industryCpdCategorys={industryCpdCategorys} />
          </Grid>
          <CpdCategorysAccordionButton {...{ additionalCpdCategorys, toggleOpen }} />
          <SetupPreferencesButton {...{ userCpdCategorys }} />
        </section> : null
      }
      {
        pendingUserTasks.length > 0 ?
          <section id='tasks-to-complete'>
            <Grid>
              <h2>Tasks to complete
                <LinkContainer className='see-all-tasks' exact to={routes.app.tasks.home}>
                  <Button>
                    See All Tasks <Icon icon='chevron-right-large' />
                  </Button>
                </LinkContainer>
              </h2>
              <PendingTasksTable tasks={pendingUserTasks} />
            </Grid>
          </section> : null
      }
      {
        <SliderSection
          id='recommended'
          className='recommended'
          heading='CPD content recommended for you'
          link={routes.app.training.home}
          linkText='See more CPD content and tasks'
          slides={recommendedResult(dashboardContent)}
          showMore='See all content'
          loading={isFetchingCpdCategorys || isFetchingPublishers || isFetchingContent} />
      }
      {
        fp.get('searchTiles.length')(dashboardEvent) > 0 ?
          <SliderSection
            id='recommended'
            className='recommended'
            heading='Events recommended for you'
            link={routes.app.training.eventHome}
            linkText='See more events'
            slides={recommendedResult(dashboardEvent)}
            showMore='See all events'
            loading={isFetchingCpdCategorys || isFetchingPublishers || isFetchingContent} /> : null
      }
      {
        fp.get('length')(nonCpdContent) > 0 ?
          <SliderSection
            id='recommended'
            className='recommended'
            heading='Business Library'
            link={routes.app.training.businessContentHome}
            linkText='See more content'
            slides={mapContent(nonCpdContent)}
            showMore='See all content' /> : null
      }
      <section>
        <Grid className='boxed'>
          <h2 className="outside-caddie">
            <Link to={routes.app.externalCpd.details}>Completed CPD outside of Caddie?</Link>
            <LinkContainer className='next pushLeft' exact to={routes.app.externalCpd.details}>
              <Button>
                Add External CPD <Icon icon='add-large' />
              </Button>
            </LinkContainer>
          </h2>
        </Grid>
      </section>
    </div>
  );

const mapStateToProps = (state) => ({
  user: currentUserSelector(state),
  cpdWeeksRemaining: currentUserCpdWeeksRemaining(state),
  cpdCategorys: state.cpdCategorys.cpdCategorys,
  isFetchingCpdCategorys: state.cpdCategorys.isFetching,
  publishers: state.publisher.publishers,
  isFetchingPublishers: state.publisher.isFetching,
  isFetchingSearchTile: state.searchTile.isFetching,
  industryCpdCategorys: state.industry.cpdCategorys,
  userCpdCategoryProgress: state.user.cpdCategoryProgress,
  isFetchingCpdCategoryProgress: state.user.isFetchingCpdCategoryProgress,
  industry: fp.first(currentUserIndustry(state)),
  isMinimumBased: isMinimumBased(currentUserIndustry(state)),
  content: contentSelector(state),
  dashboardContent: dashboardContentSelector(state),
  dashboardEvent: dashboardEventSelector(state),
  isFetchingContent: state.content.isFetching,
  userTasks: tasksSelector(state),
  business: businessSelector(state),
  nonCpdContent: publicNonCpdContentSelector(state),
  userBookmarks: state.user.currentUserBookmarks,
});

const mapDispatchToProps = (dispatch) => ({
  getTasks: bindActionCreators(taskActions.task.getTasks, dispatch),
  getDashboardContent: bindActionCreators(searchTileActions.searchTile.getDashboardContent, dispatch),
  getDashboardEvent: bindActionCreators(searchTileActions.searchTile.getDashboardEvent, dispatch),
  getUserCpdCategorys: bindActionCreators(userActions.user.getCpdCategorys, dispatch),
  getUserBookmarks: bindActionCreators(userActions.user.getUserBookmarks, dispatch),
  getPublishers: bindActionCreators(publisherActions.publishers.get, dispatch),
  getTaskCount: bindActionCreators(taskActions.task.getCount, dispatch),
  getBusinessNonCpdContent: bindActionCreators(businessActions.business.content.getNonCpdContent, dispatch),
  goTo: bindActionCreators(push, dispatch)
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withStateHandlers({
    open: false
  }, {
      toggleOpen: ({ open }) => (e) => {
        e.preventDefault();
        return { open: !open };
      }
    }),
  lifecycle({
    componentDidMount() {
      const {
        publishers,
        isFetchingPublishers,
        getPublishers,
        getUserCpdCategorys,
        getTaskCount,
        getDashboardContent,
        getDashboardEvent,
        getTasks,
        business,
        getBusinessNonCpdContent,
        getUserBookmarks,
      } = this.props;

      getTasks();
      getTaskCount();

      if (fp.isEmpty(publishers) && !isFetchingPublishers) {
        getPublishers();
      }

      if (business) {
        getBusinessNonCpdContent(business.id);
      }

      getUserCpdCategorys();
      getUserBookmarks();

      const emptyUserCpdCategorys = null;
      const emptyQuery = null;
      const emptyPublishers = null;
      const emptyBusinessId = null;
      const emptyAssociations = null;
      const emptyLocations = null;
      const emptyMinCpdHours = null;
      const emptyMaxCpdHours = null;
      const emptyContentType = null;
      const emptyIsBookmarkedOnly = null;
      getDashboardEvent(0,
        searchTileFilter(
          [searchTileType.Event, searchTileType.Course],
          [],
          'startDateAsc',
          emptyUserCpdCategorys,
          emptyQuery,
          emptyPublishers,
          emptyBusinessId,
          emptyAssociations,
          emptyLocations,
          emptyMinCpdHours,
          emptyMaxCpdHours,
          emptyContentType,
          emptyIsBookmarkedOnly,
        )
      );
      getDashboardContent(0, {
        take: 10,
        sort: ['dashboardRecommendation', 'desc'].join(','),
        searchTileType: [searchTileType.Content].join(',')
      });
    }
  }),
  branch(
    ({ user }) => isManager(user),
    renderComponent(() => (
      <Redirect to={routes.app.teams.root} />
    ))
  ),
  branch(
    ({
      isFetchingCpdCategoryProgress,
      isFetchingSearchTile,
      userCpdCategoryProgress
    }) => isFetchingCpdCategoryProgress || isFetchingSearchTile || !userCpdCategoryProgress,
    renderComponent(Loading)
  ),
  mapProps(({
    cpdCategorys,
    userCpdCategoryProgress,
    userTasks,
    user,
    ...props
  }) => ({
    ...props,
    userCpdCategoryProgress,
    userCpdCategorys: mapUserCpdCategoryProgress(cpdCategorys)(userCpdCategoryProgress),
    pendingUserTasks: fp.filter(
      ({ users }) => fp.find(
        ({ userId, status }) => userId === user.id && status !== TaskStatus.completed
      )(users)
    )(userTasks),
    user
  })),
  withProps(({
    industryCpdCategorys,
    publishers,
    business,
    userBookmarks,
  }) => ({
    mapContent: mapRecommendedContent(industryCpdCategorys, publishers, business, userBookmarks),
    recommendedResult: mapRecommendedResult(industryCpdCategorys, publishers, business, userBookmarks),
  })),
  withProps(
    ({ userCpdCategorys, industryCpdCategorys, isMinimumBased }) => ({
      userCpdCategorys: isMinimumBased ? fp.filter(
        ({ cpdCategoryId }) => {
          const nonChildCpdCategory = fp.filter(
            ({ parentCpdCategoryId }) => !parentCpdCategoryId
          )(industryCpdCategorys)
          return fp.find(
            ({ id }) => id === cpdCategoryId
          )(nonChildCpdCategory)
        }
      )(userCpdCategorys) : userCpdCategorys
    })
  ),
  withProps(({ userCpdCategorys }) => ({
    cpdCategorysChunks: fp.flow(
      fp.filter(
        ({ userId }) => userId
      ),
      fp.orderBy(['importance', 'shortFall', 'title'], ['desc', 'desc', 'asc']),
      fp.chunk(CPD_CATEGORY_CHUNK_SIZE)
    )(userCpdCategorys)
  })),
  withProps(({
    cpdCategorysChunks,
    user,
    cpdWeeksRemaining
  }) => ({
    additionalCpdCategorys: fp.tail(cpdCategorysChunks),
    userFirstName: firstName(user) || 'Anonymous',
    weeksRemaining: <WeeksRemaining {...{ cpdWeeksRemaining }} />
  }))
)(Dashboard);



// WEBPACK FOOTER //
// ./src/containers/pages/app/dashboard/dashboard.js